import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import AssignedToSelect from './AssignedToSelect';
import PrioritySelect from './PrioritySelect';
import WarrantySelect from './WarrantySelect';
import AddressFields from './AddressFields';
import DateTimeField from './DateTimeField';
import DatePickerField from './DatePickerField';
import TimePickerField from './TimePickerField';
import ServiceLocationField from './ServiceLocationField';
import CustomPhoneInput from '../../shared/PhoneInput';
import StreetAutocomplete from './StreetAutocomplete';
import { Plus } from 'lucide-react';

// Fields that should capitalize each word (names, streets)
const capitalizeWordFields = new Set(['clientName', 'clientAddress', 'street']);
// Fields that should not capitalize (phones, emails, serial numbers)
const noCapitalizeFields = new Set(['clientPhone', 'clientEmail', 'deviceSerialNumber', 'serialNumber', 'number', 'floor', 'apartment']);

const getAutoCapitalizeForField = (fieldName) => {
  if (noCapitalizeFields.has(fieldName)) {
    return 'none';
  }
  if (capitalizeWordFields.has(fieldName)) {
    return 'words';
  }
  // Default: capitalize first letter of sentences for all other text fields
  return 'sentences';
};

const FieldRenderer = ({
  fieldName,
  fieldConfig,
  value,
  onChange,
  suggestions,
  showSuggestions,
  onSuggestionSelect,
  inputClass,
  workerOptions,
  teamOptions,
  partnerOptions,
  sparePartsOptions,
  serviceLocation,
  onQuickAddSparePart,
  onQuickAddCategory,
  onQuickAddService,
  onQuickAddArrivalMethod,
  jobData,
  lockAssignmentFields = false
}) => {
  const { t } = useTranslation();
  
  // Memoize address parsing to prevent flickering - compute at top level
  const isAddressField = fieldName === 'clientAddress' || fieldName === 'clientAddress_street' || fieldName === 'clientAddress_parts';
  const addressValue = useMemo(() => {
    if (!isAddressField) return null;
    
    if (value && typeof value === 'object' && 
        ('street' in value || 'number' in value || 'floor' in value || 'apartment' in value)) {
      return {
        street: value.street || '',
        number: value.number || '',
        floor: value.floor || '',
        apartment: value.apartment || ''
      };
    } else if (typeof value === 'string' && value.trim()) {
      const parts = value.trim().split(/\s+/);
      if (parts.length >= 2) {
        return {
          street: parts.slice(0, -1).join(' '),
          number: parts[parts.length - 1],
          floor: '',
          apartment: ''
        };
      } else {
        return {
          street: value,
          number: '',
          floor: '',
          apartment: ''
        };
      }
    } else {
      return { street: '', number: '', floor: '', apartment: '' };
    }
  }, [isAddressField, value?.street, value?.number, value?.floor, value?.apartment, typeof value === 'string' ? value : null]);
  
  if (!fieldConfig) return null;

  if (fieldName === 'isBusinessClient') {
    return (
      <div className="min-h-[40px]">
        <label className="flex items-center gap-2 mt-1 text-sm font-medium text-gray-200 cursor-pointer">
          <input
            type="checkbox"
            checked={value === true}
            onChange={(e) => onChange('isBusinessClient', e.target.checked)}
            className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
          />
          <span>{t('jobs.isBusinessClient') || fieldConfig?.label}</span>
        </label>
      </div>
    );
  }

  if (fieldName.startsWith('businessClient_')) {
    const key = fieldName.replace('businessClient_', '');
    const nestedValue = value && typeof value === 'object' ? value : {};
    return (
      <div className="min-h-[57px]">
        <label className="block text-sm font-medium text-gray-200 mb-1 mt-1">
          {fieldConfig?.label || t(`jobs.${fieldName}`)} {fieldConfig?.required && '*'}
        </label>
        <input
          type={fieldConfig?.type || 'text'}
          name={fieldName}
          value={nestedValue[key] || ''}
          onChange={(e) => onChange('businessClient', { ...nestedValue, [key]: e.target.value })}
          className={inputClass}
          autoCapitalize={getAutoCapitalizeForField(fieldName)}
          autoCorrect="off"
          spellCheck={false}
          placeholder={fieldConfig?.placeholder || ''}
          required={fieldConfig?.required}
        />
      </div>
    );
  }

  if (fieldName === 'serviceLocation') {
    return (
      <ServiceLocationField
        value={serviceLocation}
        onChange={(val) => onChange('serviceLocation', val)}
        inputClass={inputClass}
      />
    );
  }

  // Handle address fields - can be split into parts for row alignment
  if (isAddressField) {
    if (serviceLocation !== 'OnSite') {
      return null;
    }
    
    if (!addressValue) return null;
    
    // Render only street part (row 3)
    if (fieldName === 'clientAddress_street') {
      return (
        <div className="min-h-[57px]">
          <label className="block text-sm font-medium text-gray-200 mb-1 mt-1">
            {t('jobs.streetAddress')}
          </label>
          <StreetAutocomplete
            value={addressValue.street}
            onChange={(newAddress) => onChange('clientAddress', { ...addressValue, ...newAddress })}
            inputClass={inputClass}
            placeholder={t('jobs.enterStreetName')}
          />
        </div>
      );
    }
    
    // Render only number/floor/apartment parts (row 4)
    if (fieldName === 'clientAddress_parts') {
      return (
        <div className="min-h-[57px]">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-200 mb-1 mt-1">
                {t('jobs.number')}
              </label>
              <input
                type="text"
                value={addressValue.number}
                onChange={(e) => onChange('clientAddress', { ...addressValue, number: e.target.value })}
                className={inputClass}
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                placeholder={t('jobs.no')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-200 mb-1 mt-1">
                {t('jobs.floor')}
              </label>
              <input
                type="text"
                value={addressValue.floor}
                onChange={(e) => onChange('clientAddress', { ...addressValue, floor: e.target.value })}
                className={inputClass}
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                placeholder={t('jobs.floor')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-200 mb-1 mt-1">
                {t('jobs.apartment')}
              </label>
              <input
                type="text"
                value={addressValue.apartment}
                onChange={(e) => onChange('clientAddress', { ...addressValue, apartment: e.target.value })}
                className={inputClass}
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                placeholder={t('jobs.apt')}
              />
            </div>
          </div>
        </div>
      );
    }
    
    // Default: render full AddressFields component
    return (
      <AddressFields
        value={addressValue}
        onChange={(val) => onChange('clientAddress', val)}
        inputClass={inputClass}
      />
    );
  }

  // Handle serviceDateTime - can be split into date and time
  if (fieldName === 'serviceDateTime' || fieldName === 'serviceDateTime_time' || fieldName === 'serviceDateTime_date') {
    // Render only date part (row 0) + X dugme – isti stil kao tip uređaja/način dolaska (visina 32px, tekst u sredini u ravni sa X)
    if (fieldName === 'serviceDateTime_date') {
      const hasDate = !!(value?.date && String(value.date).trim());
      const compactDateInputClass = 'block w-full h-8 min-h-[32px] leading-8 py-0 px-2 text-sm text-white placeholder-gray-400 bg-transparent border-0 rounded-none focus:ring-0 focus:outline-none focus:border-0';
      return (
        <div className="min-h-0">
          <label className="block text-sm font-medium text-gray-200 mb-0.5 mt-1">
            {t('jobs.serviceDate')}
          </label>
          <div className="flex items-center h-[32px] rounded-lg border border-gray-500 bg-gray-700 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 overflow-visible">
            <div className="flex-1 min-w-0 flex items-center h-full min-w-0">
              <DatePickerField
                value={value?.date || ''}
                onChange={(date) => onChange('serviceDateTime', { ...value, date: date || '' })}
                inputClass={compactDateInputClass}
                placeholder="dd.mm.yyyy"
                wrapperClassName="h-full w-full flex items-center [&_.react-datepicker-wrapper]:flex-1 [&_.react-datepicker-wrapper]:min-w-0 [&_.react-datepicker-wrapper]:h-8"
              />
            </div>
            {hasDate && (
              <button
                type="button"
                onClick={() => onChange('serviceDateTime', { ...value, date: '', time: value?.time || '' })}
                className="flex items-center justify-center w-8 h-8 flex-shrink-0 text-gray-400 hover:text-white hover:bg-gray-600 border-l border-gray-500 transition-colors text-lg leading-none"
                title={t('jobs.clearDate') || 'Poništi datum (vrati u nezakazane)'}
                aria-label={t('jobs.clearDate') || 'Poništi datum'}
              >
                ×
              </button>
            )}
          </div>
        </div>
      );
    }
    
    // Render only time part (row 2)
    if (fieldName === 'serviceDateTime_time') {
      return (
        <div className="min-h-[57px]">
          <label className="block text-sm font-medium text-gray-200 mb-1 mt-1">
            {t('jobs.serviceTime')}
          </label>
          <TimePickerField
            value={value?.time || ''}
            onChange={(time) => onChange('serviceDateTime', { ...value, time })}
            inputClass={inputClass}
            placeholder="HH:MM"
          />
        </div>
      );
    }
    
    // Default: render full DateTimeField component
    return (
      <DateTimeField
        value={value}
        onChange={(val) => onChange('serviceDateTime', val)}
        inputClass={inputClass}
      />
    );
  }

  // Handle assignedTo - can be split into worker and partner
  if (
    fieldName === 'assignedTo' ||
    fieldName === 'assignedTo_worker' ||
    fieldName === 'assignedTo_team' ||
    fieldName === 'assignedTo_partner'
  ) {
    const currentValue = value || '';
    const isWorker = currentValue.startsWith('worker:');
    const isTeam = currentValue.startsWith('team:');
    const isPartner = currentValue.startsWith('partner:');
    const workerValue = isWorker ? currentValue.replace('worker:', '') : null;
    const teamValue = isTeam ? currentValue.replace('team:', '') : null;
    const partnerValue = isPartner ? currentValue.replace('partner:', '') : null;
    
    const selectStyles = {
      control: (base) => ({
        ...base,
        minHeight: '29px',
        height: '29px',
        backgroundColor: '#374151',
        borderColor: '#4B5563',
        '&:hover': { borderColor: '#6B7280' }
      }),
      menu: (base) => ({ ...base, backgroundColor: '#374151' }),
      option: (base, state) => ({
        ...base,
        backgroundColor: state.isDisabled
          ? '#1f2937'
          : state.isFocused
            ? '#4B5563'
            : '#374151',
        color: state.isDisabled ? '#6b7280' : 'white',
        cursor: state.isDisabled ? 'not-allowed' : 'default',
        opacity: state.isDisabled ? 0.65 : 1,
        '&:hover': {
          backgroundColor: state.isDisabled ? '#1f2937' : '#4B5563'
        }
      }),
      singleValue: (base) => ({ ...base, color: 'white' }),
      input: (base) => ({ ...base, color: 'white' }),
      placeholder: (base) => ({ ...base, color: '#9CA3AF' })
    };
    
    // Render only worker part (row 2)
    if (fieldName === 'assignedTo_worker') {
      const handleWorkerChange = (selected) => {
        onChange('assignedTo', selected ? `worker:${selected.value}` : null);
      };

      return (
        <div className="min-h-[29px]">
          <Select
            value={workerOptions?.find(opt => opt.value === workerValue) || null}
            onChange={handleWorkerChange}
            options={workerOptions || []}
            isOptionDisabled={(option) => option.isDisabled}
            isDisabled={lockAssignmentFields}
            className="react-select-container"
            classNamePrefix="react-select"
            placeholder={t('jobs.selectWorker')}
            isClearable={!lockAssignmentFields}
            styles={selectStyles}
          />
        </div>
      );
    }

    if (fieldName === 'assignedTo_team') {
      const handleTeamChange = (selected) => {
        onChange('assignedTo', selected ? `team:${selected.value}` : null);
      };

      return (
        <div className="min-h-[29px]">
          <Select
            value={teamOptions?.find(opt => opt.value === teamValue) || null}
            onChange={handleTeamChange}
            options={teamOptions || []}
            isOptionDisabled={(option) => option.isDisabled}
            isDisabled={lockAssignmentFields}
            className="react-select-container"
            classNamePrefix="react-select"
            placeholder={t('jobs.selectTeam')}
            isClearable={!lockAssignmentFields}
            styles={selectStyles}
          />
        </div>
      );
    }
    
    if (fieldName === 'assignedTo_partner') {
      const handlePartnerChange = (selected) => {
        onChange('assignedTo', selected ? `partner:${selected.value}` : null);
      };

      return (
        <div className="min-h-[29px]">
          <Select
            value={partnerOptions?.find(opt => opt.value === partnerValue) || null}
            onChange={handlePartnerChange}
            options={partnerOptions || []}
            isDisabled={lockAssignmentFields}
            className="react-select-container"
            classNamePrefix="react-select"
            placeholder={t('jobs.selectPartner')}
            isClearable={!lockAssignmentFields}
            styles={selectStyles}
          />
        </div>
      );
    }
    
    return (
      <AssignedToSelect
        value={value}
        onChange={(selectedValue) => onChange('assignedTo', selectedValue || '')}
        workerOptions={workerOptions}
        teamOptions={teamOptions}
        partnerOptions={partnerOptions}
        lockAssignmentFields={lockAssignmentFields}
      />
    );
  }

  if (fieldName === 'priority') {
    return (
      <PrioritySelect
        value={value}
        onChange={(val) => onChange('priority', val)}
        inputClass={inputClass}
      />
    );
  }

  if (fieldName === 'warranty') {
    // Convert boolean to 'yes'/'no' for select
    const warrantyValue = value === true || value === 'yes' ? 'yes' : 'no';
    return (
      <WarrantySelect
        value={warrantyValue}
        onChange={(val) => {
          onChange('warranty', val === 'yes');
          // If warranty is set to 'no', clear purchase date
          if (val === 'no' && onChange) {
            onChange('warrantyPurchaseDate', '');
          }
        }}
        inputClass={inputClass}
        warrantyPurchaseDate={jobData?.warrantyPurchaseDate}
        onPurchaseDateChange={(date) => onChange('warrantyPurchaseDate', date)}
      />
    );
  }

  if (fieldName === 'usedSpareParts') {
    // Convert sparePartsOptions to react-select format
    const selectOptions = (sparePartsOptions || []).map(part => ({
      value: part.value || part._id || part.id,
      label: part.name || part.label || 'Nepoznato',
      code: part.code || ''
    }));

    // Convert value to react-select format (array of selected options)
    const selectedValues = Array.isArray(value) ? value.map(part => {
      const partId = part.value || part._id || part.id;
      const partName = part.name || part.label || 'Nepoznato';
      const partCode = part.code || '';
      return {
        value: partId,
        label: partName,
        code: partCode
      };
    }) : [];

    return (
      <div className="min-h-[40px]">
        <label className="block text-sm font-medium text-gray-200 mb-1 mt-1">
          {t('jobs.selectSpareParts') || 'Izaberi rezervni deo'} {fieldConfig?.required && '*'}
        </label>
        <div className="flex items-center">
          <div className="flex-1">
            <Select
              value={selectedValues}
              onChange={(selected) => {
                // Convert react-select format back to original format
                const newValue = selected ? selected.map(opt => ({
                  value: opt.value,
                  name: opt.label,
                  code: opt.code,
                  _id: opt.value,
                  id: opt.value
                })) : [];
                onChange('usedSpareParts', newValue);
              }}
              options={selectOptions}
              className="react-select-container"
              classNamePrefix="react-select"
              placeholder={t('common.select') || 'Izaberi...'}
              isClearable={false}
              isSearchable
              isMulti
              noOptionsMessage={() => t('common.noOptions') || 'Nema opcija'}
              styles={{
                control: (base, state) => ({
                  ...base,
                  minHeight: '29px',
                  height: '29px',
                  backgroundColor: '#374151',
                  borderColor: state.isFocused ? '#60A5FA' : '#4B5563',
                  borderTopRightRadius: '0',
                  borderBottomRightRadius: '0',
                  boxShadow: state.isFocused ? '0 0 0 1px #60A5FA' : 'none',
                  '&:hover': { borderColor: '#6B7280' }
                }),
                menu: (base) => ({ 
                  ...base, 
                  backgroundColor: '#374151',
                  zIndex: 9999,
                  borderRadius: '6px'
                }),
                menuList: (base) => ({
                  ...base,
                  padding: '4px'
                }),
                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isSelected ? '#2563EB' : state.isFocused ? '#4B5563' : '#374151',
                  color: 'white',
                  '&:active': { backgroundColor: '#2563EB' },
                  cursor: 'pointer',
                  borderRadius: '4px',
                  margin: '2px 0'
                }),
                multiValue: (base) => ({
                  ...base,
                  backgroundColor: '#4B5563',
                  borderRadius: '4px'
                }),
                multiValueLabel: (base) => ({
                  ...base,
                  color: 'white',
                  fontWeight: '500'
                }),
                multiValueRemove: (base) => ({
                  ...base,
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#6B7280',
                    color: 'white'
                  }
                }),
                input: (base) => ({ ...base, color: 'white' }),
                placeholder: (base) => ({ ...base, color: '#9CA3AF' }),
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                indicatorSeparator: (base) => ({ ...base, backgroundColor: '#4B5563' }),
                dropdownIndicator: (base) => ({ ...base, color: '#9CA3AF', '&:hover': { color: '#D1D5DB' } }),
                clearIndicator: () => ({ display: 'none' })
              }}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              menuPosition="fixed"
            />
          </div>
          {onQuickAddSparePart && (
            <button
              type="button"
              onClick={onQuickAddSparePart}
              className="px-2 h-[29px] text-sm text-white bg-blue-500 hover:bg-blue-600 border border-l-0 border-blue-500 hover:border-blue-600 rounded-r-lg transition-colors whitespace-nowrap flex-shrink-0 flex items-center justify-center"
              title={t('jobs.quickAddSparePart') || 'Dodaj rezervni deo'}
            >
              +
            </button>
          )}
        </div>
      </div>
    );
  }

  // Translation mapping for field labels and placeholders
  const getTranslatedLabel = (fieldName, defaultLabel) => {
    const translationMap = {
      'clientName': t('jobs.name'),
      'clientPhone': t('jobs.phone'),
      'clientEmail': t('jobs.email'),
      'clientAddress': t('jobs.address'),
      'isBusinessClient': t('jobs.isBusinessClient'),
      'businessClient_companyName': t('jobs.businessCompanyName'),
      'businessClient_contactPerson': t('jobs.businessContactPerson'),
      'businessClient_pib': t('jobs.businessPib'),
      'businessClient_registrationNumber': t('jobs.businessRegistrationNumber'),
      'businessClient_address': t('jobs.businessAddress'),
      'deviceType': t('jobs.deviceType'),
      'serviceArrivalMethod': t('jobs.arrivalMethod'),
      'deviceBrand': t('jobs.brand'),
      'deviceModel': t('jobs.model'),
      'deviceSerialNumber': t('jobs.serialNumber'),
      'issueDescription': t('jobs.issueDescription'),
      'priority': t('jobs.priority'),
      'warranty': t('jobs.warranty'),
      'estimatedDuration': t('jobs.duration'),
      'assignedTo': t('jobs.assignedTo'),
      'serviceDate': t('jobs.serviceDate'),
      'serviceTime': t('jobs.serviceTime'),
      'serviceDateTime': t('jobs.serviceDate') + ' & ' + t('jobs.time'),
      'notes': t('jobs.notes')
    };
    return translationMap[fieldName] || defaultLabel || fieldName;
  };

  const getTranslatedPlaceholder = (fieldName, defaultPlaceholder) => {
    const translationMap = {
      'clientName': t('jobs.enterClientName'),
      'clientPhone': t('jobs.enterPhoneNumber'),
      'clientEmail': t('jobs.enterEmailAddress'),
      'clientAddress': t('jobs.enterStreetName'),
      'businessClient_companyName': t('jobs.enterBusinessCompanyName'),
      'businessClient_contactPerson': t('jobs.enterBusinessContactPerson'),
      'businessClient_pib': t('jobs.enterBusinessPib'),
      'businessClient_registrationNumber': t('jobs.enterBusinessRegistrationNumber'),
      'businessClient_address': t('jobs.enterBusinessAddress'),
      'deviceType': t('jobs.selectDeviceType'),
      'serviceArrivalMethod': t('jobs.selectArrivalMethod'),
      'deviceBrand': t('jobs.enterDeviceBrand'),
      'deviceModel': t('jobs.enterDeviceModel'),
      'deviceSerialNumber': t('jobs.enterSerialNumber'),
      'issueDescription': t('jobs.describeIssue'),
      'priority': t('jobs.selectPriority'),
      'estimatedDuration': t('jobs.selectEstimatedRepairTime')
    };
    return translationMap[fieldName] || defaultPlaceholder || '';
  };

  // Handle textarea
  if (fieldConfig?.type === 'textarea') {
    const isNotesField = fieldName === 'notes';
    const isIssueDescription = fieldName === 'issueDescription';
    const defaultRows = isNotesField ? 3 : (fieldConfig?.rows || 3);
    // For notes field, use standard height (no extra padding)
    const textareaStyle = {};
    
    return (
      <div className={isIssueDescription ? "min-h-[57px] -mt-1" : "min-h-[57px]"}>
        <label className={`block text-sm font-medium text-gray-200 mb-1 ${isIssueDescription ? 'mt-0' : 'mt-1'}`}>
          {getTranslatedLabel(fieldName, fieldConfig?.label)} {fieldConfig?.required && '*'}
        </label>
        <textarea
          name={fieldName}
          value={value || ''}
          onChange={(e) => onChange(fieldName, e.target.value)}
          className={`${inputClass} ${fieldConfig?.className || ''}`}
          style={textareaStyle}
          autoCapitalize={getAutoCapitalizeForField(fieldName)}
          autoCorrect="off"
          spellCheck={false}
          placeholder={getTranslatedPlaceholder(fieldName, fieldConfig?.placeholder)}
          required={fieldConfig?.required}
          rows={defaultRows}
        />
      </div>
    );
  }

  // Handle select with react-select for deviceType and serviceId
  if (fieldConfig?.type === 'select' && fieldConfig?.options) {
    const showQuickAdd = (fieldName === 'deviceType' && onQuickAddCategory) || (fieldName === 'serviceId' && onQuickAddService) || (fieldName === 'serviceArrivalMethod' && onQuickAddArrivalMethod);
    
    // Use react-select for deviceType, serviceId and serviceArrivalMethod for better UI
    if (fieldName === 'deviceType' || fieldName === 'serviceId' || fieldName === 'serviceArrivalMethod') {
      const selectedOption = fieldConfig.options.find(opt => {
        // Handle both string and object values
        const optValue = typeof opt === 'object' && opt !== null ? opt.value : opt;
        const currentValue = value || '';
        // Normalize IDs for comparison
        const normalizedOptValue = String(optValue).trim();
        const normalizedCurrentValue = String(currentValue).trim();
        return normalizedOptValue === normalizedCurrentValue && normalizedCurrentValue !== '';
      });

      const hasQuickAdd = (fieldName === 'deviceType' && onQuickAddCategory) || (fieldName === 'serviceId' && onQuickAddService) || (fieldName === 'serviceArrivalMethod' && onQuickAddArrivalMethod);
      const inputGroupHeight = 30;

      return (
        <div className={hasQuickAdd ? 'min-h-0' : 'min-h-[57px]'}>
          <label className="block text-sm font-medium text-gray-200 mb-0.5 mt-1">
            {getTranslatedLabel(fieldName, fieldConfig?.label)} {fieldConfig?.required && '*'}
          </label>
          <div className={`flex items-center overflow-hidden ${hasQuickAdd ? 'rounded-lg border border-gray-500 bg-gray-700 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500' : ''}`} style={hasQuickAdd ? { height: inputGroupHeight } : undefined}>
            <div className="flex-1 min-w-0 flex items-center self-stretch">
              <Select
                key={`${fieldName}-${fieldConfig.options?.length || 0}-${value || 'empty'}`}
                value={selectedOption || null}
                onChange={(selected) => {
                  const selectedValue = selected ? selected.value : '';
                  onChange(fieldName, selectedValue);
                }}
                options={fieldConfig.options || []}
                className="react-select-container react-select-no-border react-select-compact"
                classNamePrefix="react-select"
                placeholder={t('common.select')}
                isClearable={false}
                isSearchable
                noOptionsMessage={() => t('common.noOptions') || 'Nema opcija'}
                styles={{
                  control: (base, state) => ({
                    ...base,
                    minHeight: inputGroupHeight,
                    height: inputGroupHeight,
                    backgroundColor: 'transparent',
                    border: 'none',
                    borderColor: 'transparent',
                    borderRadius: hasQuickAdd ? 0 : '6px',
                    boxShadow: 'none',
                    width: '100%',
                    '&:hover': { borderColor: 'transparent' }
                  }),
                  container: (base) => ({
                    ...base,
                    width: '100%',
                    flexGrow: 1
                  }),
                  valueContainer: (base) => ({
                    ...base,
                    padding: '0 4px',
                    height: inputGroupHeight,
                    alignItems: 'center',
                  }),
                  input: (base) => ({
                    ...base,
                    color: 'white',
                    margin: 0,
                    padding: 0,
                  }),
                  singleValue: (base) => ({
                    ...base,
                    color: 'white',
                    fontWeight: '500',
                    lineHeight: `${inputGroupHeight}px`,
                  }),
                  indicatorSeparator: () => ({ display: 'none' }),
                  dropdownIndicator: (base) => ({
                    ...base,
                    padding: '0 4px',
                    color: '#9CA3AF',
                    '&:hover': { color: '#D1D5DB' }
                  }),
                  menu: (base) => ({
                    ...base,
                    backgroundColor: '#374151',
                    zIndex: 9999,
                    borderRadius: '6px',
                    minWidth: '100%'
                  }),
                  menuList: (base) => ({ ...base, padding: '4px' }),
                  option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isSelected ? '#2563EB' : state.isFocused ? '#4B5563' : '#374151',
                    color: 'white',
                    cursor: 'pointer',
                    borderRadius: '4px',
                    margin: '2px 0'
                  }),
                  placeholder: (base) => ({ ...base, color: '#9CA3AF' }),
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  clearIndicator: () => ({ display: 'none' })
                }}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                menuPosition="fixed"
              />
            </div>
            {fieldName === 'deviceType' && onQuickAddCategory && (
              <button
                type="button"
                onClick={onQuickAddCategory}
                className="flex items-center justify-center w-7 h-7 flex-shrink-0 text-white bg-gray-600 hover:bg-gray-500 border-l border-gray-500 text-lg font-medium transition-colors leading-none"
                title={t('jobs.quickAddCategory')}
              >
                +
              </button>
            )}
            {fieldName === 'serviceId' && onQuickAddService && (
              <button
                type="button"
                onClick={onQuickAddService}
                className="flex items-center justify-center w-8 h-8 flex-shrink-0 text-white bg-gray-600 hover:bg-gray-500 border-l border-gray-500 text-lg font-medium transition-colors leading-none"
                title={t('jobs.quickAddService')}
              >
                +
              </button>
            )}
            {fieldName === 'serviceArrivalMethod' && onQuickAddArrivalMethod && (
              <button
                type="button"
                onClick={onQuickAddArrivalMethod}
                className="flex items-center justify-center w-8 h-8 flex-shrink-0 text-white bg-gray-600 hover:bg-gray-500 border-l border-gray-500 text-lg font-medium transition-colors leading-none"
                title={t('jobs.quickAddArrivalMethod')}
              >
                +
              </button>
            )}
          </div>
        </div>
      );
    }
    
    // Default select for other fields
    const isEstimatedDuration = fieldName === 'estimatedDuration';
    return (
      <div className="min-h-[40px]">
        <label className={`block text-sm font-medium text-gray-200 mb-1 ${isEstimatedDuration ? 'mt-2' : 'mt-1'}`}>
          {getTranslatedLabel(fieldName, fieldConfig?.label)} {fieldConfig?.required && '*'}
        </label>
        <select
          name={fieldName}
          value={value || ''}
          onChange={(e) => onChange(fieldName, e.target.value)}
          className={inputClass}
          required={fieldConfig?.required}
        >
          <option value="">{getTranslatedPlaceholder(fieldName, fieldConfig?.placeholder) || t('common.select')}</option>
          {fieldConfig.options.map(option => {
            // Translate option labels for device types and other common options
            const getTranslatedOptionLabel = (value, label) => {
              const deviceTypeMap = {
                'refrigerator': t('jobs.deviceTypes.refrigerator'),
                'freezer': t('jobs.deviceTypes.freezer'),
                'washingMachine': t('jobs.deviceTypes.washingMachine'),
                'dryer': t('jobs.deviceTypes.dryer'),
                'dishwasher': t('jobs.deviceTypes.dishwasher'),
                'oven': t('jobs.deviceTypes.oven'),
                'stove': t('jobs.deviceTypes.stove'),
                'microwave': t('jobs.deviceTypes.microwave'),
                'waterHeater': t('jobs.deviceTypes.waterHeater'),
                'airConditioner': t('jobs.deviceTypes.airConditioner')
              };
              const serviceTypeMap = {
                'repair': t('jobs.serviceTypes.repair'),
                'maintenance': t('jobs.serviceTypes.maintenance'),
                'installation': t('jobs.serviceTypes.installation'),
                'inspection': t('jobs.serviceTypes.inspection'),
                'consultation': t('jobs.serviceTypes.consultation'),
                'other': t('jobs.serviceTypes.other')
              };
              return deviceTypeMap[value] || serviceTypeMap[value] || label;
            };
            const optionValue = typeof option === 'object' ? option.value : option;
            const optionLabel = typeof option === 'object' ? option.label : option;
            return (
              <option key={optionValue} value={optionValue}>
                {getTranslatedOptionLabel(optionValue, optionLabel)}
              </option>
            );
          })}
        </select>
      </div>
    );
  }

  // Special handling for clientPhone with suggestions
  if (fieldName === 'clientPhone') {
    // Determine default country code from jobData or user
    let defaultCountryCode = null;
    
    // Check if jobData has countryCode
    if (jobData?.countryCode) {
      defaultCountryCode = jobData.countryCode.toLowerCase();
    }
    
    return (
      <div className="suggestions-container relative">
        <label className="block text-sm font-medium text-gray-200 mb-1 mt-1">
          {getTranslatedLabel(fieldName, fieldConfig?.label)} {fieldConfig?.required && '*'}
        </label>
        <CustomPhoneInput
          value={value || ''}
          onChange={(phoneValue) => {
            onChange(fieldName, phoneValue);
          }}
          defaultCountryCode={defaultCountryCode}
          placeholder={getTranslatedPlaceholder(fieldName, fieldConfig?.placeholder)}
          required={fieldConfig?.required}
        />
        {showSuggestions && onSuggestionSelect && suggestions && Array.isArray(suggestions) && suggestions.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-gray-800 border border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
            {suggestions.map((client, index) => (
              <div
                key={client._id || index}
                onClick={() => {
                  if (onSuggestionSelect) {
                    onSuggestionSelect(client, fieldName);
                  }
                }}
                className="px-3 py-2 hover:bg-gray-700 cursor-pointer border-b border-gray-700 last:border-b-0"
              >
                <div className="text-sm text-white font-medium">{client.name}</div>
                <div className="text-xs text-gray-400">{client.phone}</div>
                {client.email && (
                  <div className="text-xs text-gray-500">{client.email}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-[57px]" data-field-row={0}>
      <label className="block text-sm font-medium text-gray-200 mb-1 mt-1">
        {getTranslatedLabel(fieldName, fieldConfig?.label)} {fieldConfig?.required && '*'}
      </label>
      <input
        type={fieldConfig?.type || 'text'}
        name={fieldName}
        value={(value && value !== t('common.unknownBrand') && value !== t('common.unknownModel') && value !== t('common.unknownDevice') && value !== 'Unknown Brand' && value !== 'Unknown Model' && value !== 'Unknown Device') ? value : ''}
        onChange={(e) => onChange(fieldName, e.target.value)}
        className={inputClass}
        autoCapitalize={getAutoCapitalizeForField(fieldName)}
        autoCorrect="off"
        spellCheck={false}
        placeholder={getTranslatedPlaceholder(fieldName, fieldConfig?.placeholder)}
        required={fieldConfig?.required}
      />
    </div>
  );
};

export default FieldRenderer;


